import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

const CookieConsent: React.FC = () => {
  const [show, setShow] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true, // Zorunlu çerezler her zaman aktif
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Çerez tercihi kontrol et
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      setShow(true);
    } else {
      const saved = JSON.parse(cookieConsent);
      setPreferences(saved);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true
    };
    setPreferences(allAccepted);
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    setShow(false);
    setShowSettings(false);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false
    };
    setPreferences(onlyNecessary);
    localStorage.setItem('cookieConsent', JSON.stringify(onlyNecessary));
    setShow(false);
    setShowSettings(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    setShow(false);
    setShowSettings(false);
  };

  const handlePreferenceChange = (key: keyof CookiePreferences) => {
    if (key === 'necessary') return; // Zorunlu çerezler değiştirilemez
    
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <>
      {/* Ana Cookie Banner */}
      {show && !showSettings && (
        <div
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            color: 'white',
            padding: '20px',
            zIndex: 9999,
            boxShadow: '0 -4px 20px rgba(0,0,0,0.3)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-8 mb-3 mb-lg-0">
                <h5 className="fw-bold mb-2">
                  🍪 Çerez Kullanımı
                </h5>
                <p className="mb-0 small" style={{ opacity: 0.9 }}>
                  Web sitemizde deneyiminizi geliştirmek için çerezler kullanıyoruz. 
                  Zorunlu çerezler sitenin çalışması için gereklidir. 
                  Analitik ve pazarlama çerezleri için izninize ihtiyacımız var.
                  {' '}
                  <a 
                    href="/kvkk" 
                    className="text-white text-decoration-underline"
                    style={{ opacity: 0.9 }}
                  >
                    Daha fazla bilgi
                  </a>
                </p>
              </div>
              <div className="col-lg-4">
                <div className="d-flex flex-column flex-sm-row gap-2 justify-content-lg-end">
                  <Button
                    variant="outline-light"
                    size="sm"
                    onClick={() => setShowSettings(true)}
                    style={{
                      borderRadius: '20px',
                      padding: '8px 20px',
                      fontWeight: '500'
                    }}
                  >
                    Ayarlar
                  </Button>
                  <Button
                    variant="outline-light"
                    size="sm"
                    onClick={handleRejectAll}
                    style={{
                      borderRadius: '20px',
                      padding: '8px 20px',
                      fontWeight: '500'
                    }}
                  >
                    Reddet
                  </Button>
                  <Button
                    variant="light"
                    size="sm"
                    onClick={handleAcceptAll}
                    style={{
                      borderRadius: '20px',
                      padding: '8px 20px',
                      fontWeight: '600',
                      backgroundColor: '#2cadd2',
                      borderColor: '#2cadd2',
                      color: 'white'
                    }}
                  >
                    Tümünü Kabul Et
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Çerez Ayarları Modal */}
      <Modal
        show={showSettings}
        onHide={() => setShowSettings(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton style={{ borderBottom: '2px solid #2cadd2' }}>
          <Modal.Title className="fw-bold">
            🍪 Çerez Tercihleri
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <p className="text-muted mb-4">
            Çerez tercihlerinizi aşağıdan yönetebilirsiniz. Zorunlu çerezler sitenin çalışması için gereklidir ve devre dışı bırakılamaz.
          </p>

          {/* Zorunlu Çerezler */}
          <div className="mb-4 p-3 border rounded">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div>
                <h6 className="fw-bold mb-1">Zorunlu Çerezler</h6>
                <p className="small text-muted mb-0">
                  Sitenin temel işlevlerini sağlar (oturum yönetimi, güvenlik vb.)
                </p>
              </div>
              <Form.Check
                type="switch"
                checked={preferences.necessary}
                disabled
                style={{ fontSize: '1.2rem' }}
              />
            </div>
          </div>

          {/* Analitik Çerezler */}
          <div className="mb-4 p-3 border rounded">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div>
                <h6 className="fw-bold mb-1">Analitik Çerezler</h6>
                <p className="small text-muted mb-0">
                  Site kullanımını analiz eder ve deneyiminizi iyileştirir (Google Analytics vb.)
                </p>
              </div>
              <Form.Check
                type="switch"
                checked={preferences.analytics}
                onChange={() => handlePreferenceChange('analytics')}
                style={{ fontSize: '1.2rem' }}
              />
            </div>
          </div>

          {/* Pazarlama Çerezleri */}
          <div className="mb-4 p-3 border rounded">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div>
                <h6 className="fw-bold mb-1">Pazarlama Çerezleri</h6>
                <p className="small text-muted mb-0">
                  Kişiselleştirilmiş reklamlar ve içerik gösterir
                </p>
              </div>
              <Form.Check
                type="switch"
                checked={preferences.marketing}
                onChange={() => handlePreferenceChange('marketing')}
                style={{ fontSize: '1.2rem' }}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '2px solid #2cadd2' }}>
          <Button
            variant="outline-secondary"
            onClick={handleRejectAll}
            style={{
              borderRadius: '20px',
              padding: '8px 24px',
              fontWeight: '500'
            }}
          >
            Tümünü Reddet
          </Button>
          <Button
            variant="outline-primary"
            onClick={handleAcceptAll}
            style={{
              borderRadius: '20px',
              padding: '8px 24px',
              fontWeight: '500'
            }}
          >
            Tümünü Kabul Et
          </Button>
          <Button
            style={{
              backgroundColor: '#2cadd2',
              borderColor: '#2cadd2',
              borderRadius: '20px',
              padding: '8px 24px',
              fontWeight: '600'
            }}
            onClick={handleSavePreferences}
          >
            Tercihleri Kaydet
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CookieConsent;
